import {
  IconCalendarFill,
  IconCheckboxCircleFill,
  IconCloseCircleFill,
  IconFileFill,
  IconFileListAddFill,
  IconFileListCheckFill,
  IconFileListTimeFill,
  IconPaymentAdd,
  IconPaymentCheck,
  IconPaymentError,
  IconRubRound,
  IconRubRoundForbidden,
  IconRubRoundNotDefined,
  IconTimeEndFill,
  IconTimeFill,
  IconTimeWarningFill,
} from "@synthesis/shared-icons";
import { Popover, Tooltip } from "@synthesis/shared-ui";
import { cn } from "@synthesis/shared-utils";
import { Link } from "@tanstack/react-router";
import { useState, type ComponentType, type SVGProps } from "react";

import { MonitoringMetricPopover } from "./monitoring-metric-popover";
import { formatMonitoringNumber, formatMonitoringPercent, METRIC_TONE_ICON_CLASS, type MonitoringMetricItem } from "../monitoring-page.lib";

const METRIC_ICONS: Record<MonitoringMetricItem["icon"], ComponentType<SVGProps<SVGSVGElement>>> = {
  "file-fill": IconFileFill,
  "calendar-fill": IconCalendarFill,
  "checkbox-circle-fill": IconCheckboxCircleFill,
  "close-circle-fill": IconCloseCircleFill,
  "rub-round": IconRubRound,
  "rub-round-forbidden": IconRubRoundForbidden,
  "rub-round-not-defined": IconRubRoundNotDefined,
  "payment-add": IconPaymentAdd,
  "payment-check": IconPaymentCheck,
  "payment-error": IconPaymentError,
  "time-fill": IconTimeFill,
  "time-warning-fill": IconTimeWarningFill,
  "time-end-fill": IconTimeEndFill,
  "file-list-add-fill": IconFileListAddFill,
  "file-list-time-fill": IconFileListTimeFill,
  "file-list-check-fill": IconFileListCheckFill,
};

type MonitoringMetricProps = {
  metric: MonitoringMetricItem;
  className?: string;
  percentPlacement?: "stack" | "inline";
};

const MonitoringMetricContent = ({ metric, percentPlacement }: { metric: MonitoringMetricItem; percentPlacement: "stack" | "inline" }) => {
  const Icon = METRIC_ICONS[metric.icon];
  const percent = formatMonitoringPercent(metric.percent);

  return (
    <>
      <Icon className={cn("size-8 shrink-0", METRIC_TONE_ICON_CLASS[metric.tone])} aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="subtitle-lg text-type-common">{formatMonitoringNumber(metric.value)}</p>
        {percentPlacement === "inline" ? (
          <div className="flex items-center justify-between gap-2">
            <p className="subtitle-sm text-type-minor truncate">{metric.label}</p>
            <p className="subtitle-sm text-type-minor shrink-0">{percent}</p>
          </div>
        ) : (
          <>
            <p className="subtitle-sm text-type-minor truncate">{metric.label}</p>
            <div className="flex items-center justify-between gap-2">
              <p className="subtitle-sm text-type-minor">{percent}</p>
              {metric.hint ? <p className="subtitle-sm text-type-light shrink-0">{metric.hint}</p> : null}
            </div>
          </>
        )}
      </div>
    </>
  );
};

export const MonitoringMetric = ({ metric, className, percentPlacement = "stack" }: MonitoringMetricProps) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const interactiveClassName = cn(
    "border-neutrals-90 flex w-full min-w-0 cursor-pointer items-center gap-2 rounded-t-xl border-b-2 pb-2 pl-0.5 text-left",
    "hover:bg-neutrals-99",
    isPopoverOpen && "bg-neutrals-99",
  );
  const wrapperClassName = cn(percentPlacement === "stack" ? "min-w-0 flex-1" : "w-full", className);

  const trigger = (
    <Tooltip description={metric.tooltip} arrowSide="top" arrowAlign="center" size="sm" disabled={isPopoverOpen}>
      {metric.interaction === "registry" ? (
        <Link to="/" search={{ monitoringMetric: metric.id, railway: metric.railwayId }} className={interactiveClassName}>
          <MonitoringMetricContent metric={metric} percentPlacement={percentPlacement} />
        </Link>
      ) : (
        <button type="button" className={interactiveClassName} aria-haspopup="dialog" aria-expanded={isPopoverOpen}>
          <MonitoringMetricContent metric={metric} percentPlacement={percentPlacement} />
        </button>
      )}
    </Tooltip>
  );

  if (metric.interaction === "popover") {
    return (
      <div className={wrapperClassName}>
        <Popover
          trigger={trigger}
          open={isPopoverOpen}
          setIsOpen={setIsPopoverOpen}
          onOpenAutoFocus={(event) => event.preventDefault()}
          isArrow={false}
          align="start"
          side="bottom"
          className="w-[52.375rem] max-w-[calc(100vw-2rem)] overflow-hidden p-0"
        >
          <MonitoringMetricPopover metric={metric} enabled={isPopoverOpen} />
        </Popover>
      </div>
    );
  }

  return <div className={wrapperClassName}>{trigger}</div>;
};
